import { BrowserAuthError, InteractionRequiredAuthError } from '@azure/msal-browser';

import { msalInstance, msalScopes } from '~config';
import {
  clearAuthenticatedUserContext,
  setAuthenticatedUserContext,
  trackEvent,
  trackException,
} from '~libs/telemetry';
import { useAuthStore } from '~store';

/* -------------------------------------------------------------------------- */
/*                                 Constants                                  */
/* -------------------------------------------------------------------------- */

/** Shared promise to prevent multiple simultaneous token refreshes */
let tokenRefreshPromise: Promise<string | null> | null = null;

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

/**
 * Returns true when the error requires interactive login rather than silent retry.
 * Covers InteractionRequiredAuthError (expired refresh token) and
 * block_iframe_reload (MSAL trying to silently re-auth inside an iframe loop).
 */
const requiresInteractiveAuth = (error: unknown): boolean =>
  error instanceof InteractionRequiredAuthError ||
  (error instanceof BrowserAuthError && error.errorCode === 'block_iframe_reload');

/* -------------------------------------------------------------------------- */
/*                                Main service                                */
/* -------------------------------------------------------------------------- */

/**
 * Centralized authentication service for managing MSAL interactions and auth state.
 * Handles token acquisition, caching, and automatic refresh on expiration.
 * Also provides a method to initialize auth state from MSAL after login.
 *
 * The getValidToken method ensures that only one token refresh is in-flight at a time,
 * preventing multiple simultaneous refreshes when multiple API calls are made with an expired token.
 */
const authService = {
  /** Initialize auth state from MSAL (call once after authentication) */
  async initializeFromMsal(): Promise<void> {
    try {
      const msalAccount = msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
      if (!msalAccount) return;

      const currentStoreAccount = useAuthStore.getState().account;
      // Skip if already initialized with the same account
      if (currentStoreAccount?.homeAccountId === msalAccount.homeAccountId) return;

      // If different account, clear old state first
      if (currentStoreAccount) {
        useAuthStore.getState().clearAuth();
      }

      msalInstance.setActiveAccount(msalAccount);
      const tokenResponse = await msalInstance.acquireTokenSilent({
        account: msalAccount,
        scopes: [...msalScopes.login],
      });

      if (tokenResponse.accessToken && tokenResponse.expiresOn) {
        useAuthStore.getState().setAuth(msalAccount, tokenResponse.accessToken, tokenResponse.expiresOn.getTime());
        setAuthenticatedUserContext(msalAccount.homeAccountId);
        trackEvent('session_started', {
          homeAccountId: msalAccount.homeAccountId,
          name: msalAccount.name,
          username: msalAccount.username,
        });
      }
    } catch (error) {
      if (requiresInteractiveAuth(error)) {
        // Refresh token expired or iframe loop detected — force a full interactive login
        msalInstance.acquireTokenRedirect({ scopes: [...msalScopes.login] }).catch((redirectError) => {
          trackException(redirectError, { source: 'auth.initializeFromMsal.redirect' });
        });
        return;
      }
      console.warn('Failed to initialize auth from MSAL:', error);
      trackException(error, { source: 'auth.initializeFromMsal' });
    }
  },

  /** Gets a valid access token, refreshing if necessary */
  async getValidToken(): Promise<string | null> {
    const account = useAuthStore.getState().account;
    const accessToken = useAuthStore.getState().accessToken;
    const isTokenValid = useAuthStore.getState().isTokenValid;
    const setAuth = useAuthStore.getState().setAuth;
    const clearAuth = useAuthStore.getState().clearAuth;

    // Return cached token if still valid
    if (accessToken && isTokenValid()) return accessToken;

    // Return existing refresh promise if one is in progress
    if (tokenRefreshPromise) return tokenRefreshPromise;

    // No account means not authenticated, nothing to refresh
    if (!account) {
      clearAuth();
      return null;
    }

    // Create new token refresh promise
    tokenRefreshPromise = (async () => {
      try {
        if (!msalInstance.getActiveAccount()) {
          msalInstance.setActiveAccount(account);
        }

        const tokenResponse = await msalInstance.acquireTokenSilent({
          account,
          scopes: [...msalScopes.login],
        });

        if (tokenResponse.accessToken && tokenResponse.expiresOn) {
          setAuth(account, tokenResponse.accessToken, tokenResponse.expiresOn.getTime());
          setAuthenticatedUserContext(account.homeAccountId);
          return tokenResponse.accessToken;
        }

        clearAuth();
        return null;
      } catch (error) {
        if (requiresInteractiveAuth(error)) {
          // Don't clear the account — trigger interactive re-auth to break the stale session
          msalInstance.acquireTokenRedirect({ account, scopes: [...msalScopes.login] }).catch((redirectError) => {
            trackException(redirectError, { source: 'auth.getValidToken.redirect' });
            clearAuth();
          });
          return null;
        }
        console.error('Token refresh failed:', error);
        trackException(error, { source: 'auth.getValidToken' });
        clearAuth();
        return null;
      } finally {
        tokenRefreshPromise = null; // Always clear the promise
      }
    })();

    return tokenRefreshPromise;
  },

  /** Clears authentication state */
  clearAuth(): void {
    tokenRefreshPromise = null; // Clear any pending refresh
    clearAuthenticatedUserContext();
    useAuthStore.getState().clearAuth();
  },

  /** Invalidates only the cached token without clearing the account, allowing silent refresh on the next request */
  invalidateToken(): void {
    tokenRefreshPromise = null; // Clear any pending refresh
    useAuthStore.getState().clearToken();
  },
};

/* -------------------------------------------------------------------------- */
/*                                   Exports                                  */
/* -------------------------------------------------------------------------- */

export { authService, requiresInteractiveAuth };
